'use client';

import React, { useEffect, useState, useCallback } from 'react';
import type { Reminder } from '@/core/domain';
import { parseBrlMoneyStringToCents } from '@/core/domain';
import { Button } from '@/core/presentation/components/ui/Button';
import {
  LEMBRETES_SCHEDULE_CANCEL,
  LEMBRETES_SCHEDULE_ERR_DATE,
  LEMBRETES_SCHEDULE_ERR_VALUE,
  LEMBRETES_SCHEDULE_FIELD_DATE,
  LEMBRETES_SCHEDULE_FIELD_VALUE,
  LEMBRETES_SCHEDULE_FIELD_VALUE_HINT,
  LEMBRETES_SCHEDULE_MODAL_INTRO,
  LEMBRETES_SCHEDULE_MODAL_TITLE,
  LEMBRETES_SCHEDULE_SAVING,
  LEMBRETES_SCHEDULE_SUBMIT,
} from '@/features/app/presentation/constants/lembretes-calendario-copy';
import { FORM_FIELD_CONTROL_APP, FORM_FIELD_LABEL_APP } from '@/core/presentation/constants/form-field-ui.constants';

export interface ScheduleMaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  reminder: Reminder | null;
  submitting: boolean;
  /** Retorna true se salvou com sucesso (fecha o modal). */
  onSubmit: (dateKey: string, valueReais: number) => Promise<boolean>;
  onValidationError?: (message: string) => void;
}

const inputClass = FORM_FIELD_CONTROL_APP;

export const ScheduleMaintenanceModal: React.FC<ScheduleMaintenanceModalProps> = ({
  isOpen,
  onClose,
  reminder,
  submitting,
  onSubmit,
  onValidationError,
}) => {
  const [dateKey, setDateKey] = useState('');
  const [valueText, setValueText] = useState('');

  useEffect(() => {
    if (!isOpen || !reminder) return;
    const d = reminder.expected_date.trim().slice(0, 10);
    setDateKey(d.length >= 10 ? d : '');
    if (reminder.value_charged != null && Number.isFinite(reminder.value_charged)) {
      setValueText(
        reminder.value_charged.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    } else {
      setValueText('');
    }
  }, [isOpen, reminder]);

  const handleSubmit = useCallback(async () => {
    if (!dateKey || dateKey.length < 10) {
      onValidationError?.(LEMBRETES_SCHEDULE_ERR_DATE);
      return;
    }
    const t = valueText.trim();
    if (t === '') {
      onValidationError?.(LEMBRETES_SCHEDULE_ERR_VALUE);
      return;
    }
    const cents = parseBrlMoneyStringToCents(t);
    if (cents === null) {
      onValidationError?.(LEMBRETES_SCHEDULE_ERR_VALUE);
      return;
    }
    const valueReais = cents / 100;
    const ok = await onSubmit(dateKey, valueReais);
    if (ok) onClose();
  }, [dateKey, valueText, onSubmit, onClose, onValidationError]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, submitting]);

  if (!isOpen || !reminder) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-maintenance-title"
    >
      <button
        type="button"
        aria-label={LEMBRETES_SCHEDULE_CANCEL}
        disabled={submitting}
        onClick={onClose}
        className="absolute inset-0 bg-black/50 transition-opacity disabled:pointer-events-none"
      />
      <div
        className="relative w-full sm:max-w-md max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-xl bg-white border border-border-light shadow-xl flex flex-col mt-auto sm:mt-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 sm:px-6 py-4 border-b border-border-light shrink-0">
          <h2 id="schedule-maintenance-title" className="text-lg font-semibold text-navy-dark pr-8">
            {LEMBRETES_SCHEDULE_MODAL_TITLE}
          </h2>
          <p className="text-sm text-text-muted mt-1 leading-relaxed">{LEMBRETES_SCHEDULE_MODAL_INTRO}</p>
        </div>
        <form
          className="flex flex-col flex-1 min-h-0"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4">
            <div>
              <label htmlFor="schedule-maintenance-date" className={FORM_FIELD_LABEL_APP}>
                {LEMBRETES_SCHEDULE_FIELD_DATE}
              </label>
              <input
                id="schedule-maintenance-date"
                type="date"
                className={inputClass}
                value={dateKey}
                onChange={(e) => setDateKey(e.target.value)}
                disabled={submitting}
                required
              />
            </div>
            <div>
              <label htmlFor="schedule-maintenance-value" className={FORM_FIELD_LABEL_APP}>
                {LEMBRETES_SCHEDULE_FIELD_VALUE}
              </label>
              <input
                id="schedule-maintenance-value"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder={LEMBRETES_SCHEDULE_FIELD_VALUE_HINT}
                className={inputClass}
                value={valueText}
                onChange={(e) => setValueText(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>
          <div className="px-4 sm:px-6 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:pb-4 border-t border-border-light shrink-0 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full sm:w-auto sm:min-w-[7rem]"
              disabled={submitting}
              onClick={onClose}
            >
              {LEMBRETES_SCHEDULE_CANCEL}
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              className="w-full sm:w-auto sm:min-w-[10rem]"
              disabled={submitting}
            >
              {submitting ? LEMBRETES_SCHEDULE_SAVING : LEMBRETES_SCHEDULE_SUBMIT}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
